/**
 * View model builder. This is the translation layer between raw schema+data
 * and what the UI renders. Keep it pure: same inputs -> same output, no I/O.
 *
 * Design note: we build ONE level at a time. The UI zooms into a pointer,
 * we build the view for that pointer, render it. Descending recursively up
 * front would mean building the entire tree eagerly for every edit — fine
 * for small schemas, catastrophic for a CMS with hundreds of pages.
 */

import type { JsonSchema, JsonValue } from '@bliksys/shared';

import { SchemaEngineError } from './errors.js';
import { appendPointer, type JsonPointer } from './pointer.js';
import { isConstraintUnion, inferDiscriminator, readConstProperty } from './predicates.js';
import { resolveSchema, type RefRegistry } from './resolver.js';
import type {
  ArrayItemDescriptor,
  ArrayView,
  BooleanView,
  EnumView,
  FieldDescriptor,
  NumberView,
  ObjectFieldGroup,
  ObjectView,
  OneOfView,
  OneOfVariant,
  StringView,
  UnsupportedView,
  ViewNode,
} from './view-model.js';
import { resolveAndKeepAnnotations, schemaAtPointer } from './walker.js';

export interface BuildContext {
  /** The full schema — needed for $ref resolution. */
  rootSchema: JsonSchema;
  /** The full data document — needed for pointer resolution. */
  rootData: JsonValue;
  /**
   * Optional registry of remote-ref schemas. When the schema being
   * built contains `$ref: 'https://example.com/foo.json#/Foo'`, the
   * resolver consults this registry by URL. Without a registry, remote
   * refs throw `UnsupportedSchemaError`.
   *
   * Populated by the host app: the editor scans the root schema for
   * remote refs on project open, fetches each, and stashes the result
   * here. A failed fetch leaves the URL absent — the resolver then
   * surfaces a clear "schema not loaded" message rather than crashing.
   */
  refRegistry?: RefRegistry;
}

/**
 * Build a view model for the node at `pointer`. The caller only needs to know
 * the root schema and data — the engine figures out which subschema applies.
 *
 * If the pointer is unreachable (e.g. the URL was deep-linked to a path whose
 * intermediate oneOf has no variant selected), we return an `unsupported` node
 * rather than throwing. The UI renders that as a recoverable message with a
 * "go back" affordance, which is far better than a blank screen.
 */
export function buildView(pointer: JsonPointer, ctx: BuildContext): ViewNode {
  try {
    const { schema, value } = schemaAtPointer(
      pointer,
      ctx.rootSchema,
      ctx.rootData,
      ctx.refRegistry,
    );
    return buildNode(pointer, schema, value, ctx, /* required */ false);
  } catch (err) {
    if (err instanceof SchemaEngineError) {
      return {
        pointer,
        label: 'Unreachable',
        description: undefined,
        required: false,
        kind: 'unsupported',
        zoomable: false,
        reason: err.message,
        rawValue: undefined,
      };
    }
    throw err;
  }
}

function buildNode(
  pointer: JsonPointer,
  schema: JsonSchema,
  value: JsonValue | undefined,
  ctx: BuildContext,
  required: boolean,
): ViewNode {
  const label =
    schema['x-bliksys']?.label ??
    schema.title ??
    labelFromValue(pointer, value, schema) ??
    defaultLabel(pointer);
  const description = schema.description;
  const base = { pointer, label, description, required };

  // oneOf / anyOf — we treat anyOf like oneOf for UI purposes; a picky validator
  // can enforce the distinction, but the user experience is "pick a variant".
  //
  // EXCEPTION: a "constraint-union" oneOf, where every variant only refines
  // the parent's existing shape (e.g. alternative `format`s on a string),
  // isn't a tagged union — the user never picks a variant. Fall through so
  // the parent's primitive type renders as a leaf with the alternative
  // formats surfaced in the label/description.
  const variants = schema.oneOf ?? schema.anyOf;
  if (variants && variants.length > 0 && !isConstraintUnion(variants)) {
    return buildOneOfView(base, schema, variants, value, ctx);
  }

  if (schema.enum && schema.enum.length > 0) {
    return {
      ...base,
      kind: 'enum',
      zoomable: false,
      value: value ?? null,
      options: schema.enum,
    } satisfies EnumView;
  }

  const type = normalizeType(schema);

  if (type === 'object') return buildObjectView(base, schema, value, ctx);
  if (type === 'array') return buildArrayView(base, schema, value, ctx);
  if (type === 'string') return buildStringView(base, schema, value);
  if (type === 'number' || type === 'integer') {
    return buildNumberView(base, schema, value, type === 'integer');
  }
  if (type === 'boolean') return buildBooleanView(base, value);

  return {
    ...base,
    kind: 'unsupported',
    zoomable: false,
    reason: `Schema has no recognizable type${schema.type ? `: ${String(schema.type)}` : ''}`,
    rawValue: value,
  } satisfies UnsupportedView;
}

interface Base {
  pointer: JsonPointer;
  label: string;
  description: string | undefined;
  required: boolean;
}

function buildObjectView(
  base: Base,
  schema: JsonSchema,
  value: JsonValue | undefined,
  ctx: BuildContext,
): ObjectView {
  const properties = schema.properties ?? {};
  const requiredSet = new Set(schema.required ?? []);
  const obj = isPlainObject(value) ? value : {};

  const allEntries = Object.entries(properties).filter(([, propSchema]) => {
    const resolved = resolveSchema(propSchema, ctx.rootSchema, undefined, ctx.refRegistry);
    if (resolved['x-bliksys']?.hidden === true) return false;
    // Hide fields the schema pins to a single value. They can't be
    // edited meaningfully, and for discriminator-style tags (`type: {
    // enum: ["nfs"] }`) editing them would corrupt the parent oneOf.
    // The engine already writes the correct constant via defaultValueFor.
    return !isPinnedToSingleValue(resolved);
  });

  // Field order: when the schema declares `x-bliksys.propOrder`, that's the
  // authoritative order. Listed keys appear in the listed sequence;
  // unlisted keys keep their insertion order and append at the end.
  // Without the hint, insertion order of the `properties` map is used —
  // JS preserves this on Object.entries.
  //
  // The hint exists because not every JSON Schema producer preserves
  // key order across serialization (some sort alphabetically), so a
  // schema author who cares about field order in the editor should be
  // able to express it explicitly. The schema editor's reorder
  // affordances write to this hint when present and the data shape
  // would otherwise lose order.
  const propOrder = schema['x-bliksys']?.propOrder;
  const entries = Array.isArray(propOrder) ? sortByPropOrder(allEntries, propOrder) : allEntries;

  const fields: FieldDescriptor[] = entries.map(([key, propSchema]) => {
    // Use the annotation-preserving resolver so a property authored as
    // `{ title: 'If', $ref: '#/$defs/Schema' }` keeps the wrapper-side
    // title — without this, the resolver returns the target Schema's
    // own "Schema" title and the field renders as a row labeled "Schema"
    // instead of "If" / "Then" / "Else" / etc. Same fix as the walker
    // path; this is a separate code path (parent describing its child)
    // that needs the same treatment.
    const resolved = resolveAndKeepAnnotations(propSchema, ctx.rootSchema, ctx.refRegistry);
    const childPointer = appendPointer(base.pointer, key);
    const childValue = obj[key];
    return {
      key,
      pointer: childPointer,
      label: resolved['x-bliksys']?.label ?? resolved.title ?? humanize(key),
      description: resolved.description,
      required: requiredSet.has(key),
      zoomable: isZoomable(resolved, ctx.rootSchema),
      preview: makePreview(childValue, resolved),
      schema: resolved,
      isAdditional: false,
      layout: resolved['x-bliksys']?.layout,
    };
  });

  // Surface keys present in the data that aren't named in `properties`,
  // when the schema declares an `additionalProperties` schema for them.
  // This is what powers the schema editor's edit-a-property-map UX
  // (e.g. `properties: { ... }` is itself a Record<string, Schema>).
  const additionalPropsSchema =
    typeof schema.additionalProperties === 'object' && schema.additionalProperties !== null
      ? resolveAndKeepAnnotations(schema.additionalProperties, ctx.rootSchema, ctx.refRegistry)
      : undefined;
  if (additionalPropsSchema) {
    const known = new Set(Object.keys(properties));
    for (const key of Object.keys(obj)) {
      if (known.has(key)) continue;
      const childPointer = appendPointer(base.pointer, key);
      const childValue = obj[key];
      fields.push({
        key,
        pointer: childPointer,
        label: humanize(key),
        description: additionalPropsSchema.description,
        required: false,
        zoomable: isZoomable(additionalPropsSchema, ctx.rootSchema),
        preview: makePreview(childValue, additionalPropsSchema),
        schema: additionalPropsSchema,
        isAdditional: true,
        layout: additionalPropsSchema['x-bliksys']?.layout,
      });
    }
  }

  // Compute field-group metadata. The schema author declares groups
  // via `x-bliksys.fieldGroups`; we surface them as-is for the renderer
  // and additionally compute which fields are "ungrouped" (so the
  // renderer can show them under an implicit "Other" section without
  // having to do the diff itself).
  const groupsHint = schema['x-bliksys']?.fieldGroups;
  const { fieldGroups, ungroupedFields } = resolveFieldGroups(groupsHint, fields);

  return {
    ...base,
    kind: 'object',
    zoomable: false, // when you ARE the object view you're not zooming further
    fields,
    summary: makeSummary(obj, schema),
    additionalPropertySchema: additionalPropsSchema,
    fieldGroups,
    ungroupedFields,
  };
}

function resolveFieldGroups(
  hint:
    | readonly {
        id: string;
        title: string;
        description?: string;
        fields: readonly string[];
        collapsed?: boolean;
      }[]
    | undefined,
  fields: FieldDescriptor[],
): { fieldGroups: ObjectFieldGroup[] | undefined; ungroupedFields: readonly string[] } {
  if (hint === undefined) {
    return { fieldGroups: undefined, ungroupedFields: [] };
  }
  const presentKeys = new Set(fields.map((f) => f.key));
  const grouped = new Set<string>();
  const fieldGroups: ObjectFieldGroup[] = hint.map((g) => {
    // Filter the group's declared fields to those actually present in
    // the view. A group might list a field that's only relevant for a
    // sibling variant (e.g. "items" listed under "Structure" when this
    // is an object schema); skipping absent fields keeps the section
    // tidy without forcing the schema author to maintain per-variant
    // group definitions.
    const fields = g.fields.filter((k) => {
      if (!presentKeys.has(k)) return false;
      grouped.add(k);
      return true;
    });
    return {
      id: g.id,
      title: g.title,
      description: g.description,
      fields,
      collapsedByDefault: g.collapsed ?? false,
    };
  });
  const ungroupedFields = fields.map((f) => f.key).filter((k) => !grouped.has(k));
  return { fieldGroups, ungroupedFields };
}

function buildArrayView(
  base: Base,
  schema: JsonSchema,
  value: JsonValue | undefined,
  ctx: BuildContext,
): ArrayView {
  const itemSchemaRaw = schema.items ?? {};
  const itemSchema = resolveSchema(itemSchemaRaw, ctx.rootSchema, undefined, ctx.refRegistry);
  const arr = Array.isArray(value) ? value : [];

  const items: ArrayItemDescriptor[] = arr.map((itemValue, index) => {
    const itemPointer = appendPointer(base.pointer, index);
    // For tagged-union items, resolve the active variant so the summary reflects it.
    const effectiveSchema = resolveActiveVariant(itemSchema, itemValue, ctx) ?? itemSchema;
    return {
      index,
      pointer: itemPointer,
      summary: makeSummary(itemValue, effectiveSchema, index),
      discriminatorValue: readDiscriminator(itemValue, itemSchema),
    };
  });

  // Items are "scalar" when each item is a leaf type (no recursion).
  // For oneOf, this means every variant must itself resolve to a scalar
  // — used by the meta-schema's `enum` field whose items are
  // `{ oneOf: [{type:'string'}, {type:'number'}, ...] }`. Without this
  // recursion, the chip editor falls back to zoom-row UX for what is
  // really just a list of primitive values.
  const itemsAreScalar = isScalarSchema(itemSchema, ctx.rootSchema);

  return {
    ...base,
    kind: 'array',
    zoomable: false,
    items,
    itemSchema,
    minItems: schema.minItems,
    maxItems: schema.maxItems,
    itemsAreScalar,
    widget: schema['x-bliksys']?.widget,
    optionsFromSibling: schema['x-bliksys']?.optionsFromSibling,
  };
}

function buildStringView(base: Base, schema: JsonSchema, value: JsonValue | undefined): StringView {
  const widgetHint = schema['x-bliksys']?.widget;
  const widget: StringView['widget'] =
    widgetHint === 'textarea' ||
    widgetHint === 'markdown' ||
    widgetHint === 'code' ||
    widgetHint === 'color' ||
    widgetHint === 'image'
      ? widgetHint
      : schema.format === 'uri' && looksLikeImage(asString(value))
        ? 'image'
        : (schema.maxLength ?? 0) > 200
          ? 'textarea'
          : 'text';

  const constraintVariants = schema.oneOf ?? schema.anyOf;
  const acceptedFormats =
    constraintVariants && isConstraintUnion(constraintVariants)
      ? constraintVariants
          .map((v) => v.format)
          .filter((f): f is NonNullable<typeof f> => f !== undefined)
      : undefined;

  return {
    ...base,
    kind: 'string',
    zoomable: widget === 'markdown' || widget === 'code',
    value: asString(value) ?? '',
    widget,
    placeholder: schema['x-bliksys']?.placeholder,
    minLength: schema.minLength,
    maxLength: schema.maxLength,
    pattern: schema.pattern,
    format: schema.format,
    acceptedFormats: acceptedFormats && acceptedFormats.length > 0 ? acceptedFormats : undefined,
    refsFrom: schema['x-bliksys']?.refsFrom,
    discriminatorCandidatesFrom: schema['x-bliksys']?.discriminatorCandidatesFrom,
    assetFormat: schema['x-bliksys']?.assetFormat ?? 'url',
  };
}

function buildNumberView(
  base: Base,
  schema: JsonSchema,
  value: JsonValue | undefined,
  integer: boolean,
): NumberView {
  return {
    ...base,
    kind: 'number',
    zoomable: false,
    value: typeof value === 'number' ? value : null,
    integer,
    minimum: schema.minimum ?? schema.exclusiveMinimum,
    maximum: schema.maximum ?? schema.exclusiveMaximum,
    multipleOf: schema.multipleOf,
  };
}

function buildBooleanView(base: Base, value: JsonValue | undefined): BooleanView {
  return {
    ...base,
    kind: 'boolean',
    zoomable: false,
    value: typeof value === 'boolean' ? value : null,
  };
}

function buildOneOfView(
  base: Base,
  schema: JsonSchema,
  variants: JsonSchema[],
  value: JsonValue | undefined,
  ctx: BuildContext,
): OneOfView {
  const resolvedSubschemas = variants.map((v) =>
    resolveSchema(v, ctx.rootSchema, undefined, ctx.refRegistry),
  );
  // Prefer an explicit OpenAPI-style discriminator; otherwise infer one by
  // looking for a shared property that every variant pins to a constant
  // value. Real-world JSON Schemas (fstab, many OpenAPI-derived shapes)
  // rarely declare `discriminator` even when a `type` field plays that role.
  const discriminator =
    schema.discriminator?.propertyName ?? inferDiscriminator(resolvedSubschemas);
  const resolvedVariants: OneOfVariant[] = resolvedSubschemas.map((resolved, idx) => {
    const discValue = discriminator ? readConstProperty(resolved, discriminator) : undefined;
    const unresolved = variants[idx]!;
    return {
      label:
        // The unresolved wrapper's own label/title wins. This matters for
        // `{ $ref: '...', title: 'Constrain to schema' }` — the resolver
        // follows $ref and returns the target schema's properties, so any
        // wrapper-side title is lost. Read it here before that happens.
        unresolved['x-bliksys']?.label ??
        unresolved.title ??
        resolved['x-bliksys']?.label ??
        resolved.title ??
        labelFromRef(unresolved) ??
        discValue ??
        labelFromPrimitiveType(resolved) ??
        `Option ${idx + 1}`,
      discriminatorValue: discValue,
      schema: resolved,
      // Description and icon: same wrapper-wins pattern as the label.
      // Authors set these on the variant slot in the parent oneOf, not
      // on the referenced definition (the definition is reused, the
      // hint is contextual to this use site).
      description: unresolved.description ?? resolved.description,
      icon: unresolved['x-bliksys']?.variantIcon ?? resolved['x-bliksys']?.variantIcon,
    };
  });

  const activeVariant = pickVariant(resolvedVariants, value, discriminator);

  // When a variant is active, build the child view against the variant's
  // schema RIGHT HERE. Callers must never recursively call buildView on
  // the same pointer expecting to escape the oneOf — that loops forever.
  const resolvedView: ViewNode | null =
    activeVariant !== null
      ? buildNode(base.pointer, resolvedVariants[activeVariant]!.schema, value, ctx, base.required)
      : null;

  return {
    ...base,
    kind: 'oneOf',
    zoomable: true,
    discriminator,
    variants: resolvedVariants,
    activeVariant,
    resolvedView,
    discriminatorLocked: schema['x-bliksys']?.discriminatorLocked === true,
    pickerLayout: schema['x-bliksys']?.variantPickerLayout ?? 'select',
  };
}

/* ---------------- helpers ---------------- */

function normalizeType(schema: JsonSchema): JsonSchema['type'] | undefined {
  const type = schema.type;
  if (type === undefined) {
    // Infer from shape when missing — JSON Schemas in the wild (fstab, many
    // OpenAPI-derived schemas) omit `type: 'object'` whenever `properties`
    // is present. Without inference, buildNode produces `unsupported` views
    // for perfectly valid schemas.
    if (schema.properties) return 'object';
    if (schema.items) return 'array';
    return undefined;
  }
  if (Array.isArray(type)) {
    // Ignore 'null' unions — they mean "nullable", not "pick a type".
    const nonNull = type.filter((t) => t !== 'null');
    return nonNull.length === 1 ? nonNull[0] : undefined;
  }
  return type;
}

function isPlainObject(value: JsonValue | undefined): value is Record<string, JsonValue> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function asString(value: JsonValue | undefined): string | null {
  return typeof value === 'string' ? value : null;
}

function isZoomable(schema: JsonSchema, rootSchema: JsonSchema): boolean {
  const type = normalizeType(schema);
  if (type === 'object') return true;
  const unionVariants = schema.oneOf ?? schema.anyOf;
  if (unionVariants && !isConstraintUnion(unionVariants)) return true;
  if (type === 'array') {
    // An array of primitives doesn't deserve its own page — it renders
    // inline as a compact chip list. Only arrays of objects/unions/nested
    // arrays are zoomable.
    if (!schema.items) return true;
    const items = resolveSchema(schema.items, rootSchema);
    const itemType = normalizeType(items);
    if (items.oneOf || items.anyOf) return true;
    return itemType === 'object' || itemType === 'array';
  }
  return false;
}

// Acronyms that should be preserved verbatim instead of being letter-spaced
// by the camel-case splitter (`UUID` -> `U U I D`). Lowercase keys; output
// is the canonical casing the user expects.
const ACRONYMS: Record<string, string> = {
  uuid: 'UUID',
  ipv4: 'IPv4',
  ipv6: 'IPv6',
  url: 'URL',
  uri: 'URI',
  http: 'HTTP',
  https: 'HTTPS',
  api: 'API',
  json: 'JSON',
  xml: 'XML',
  html: 'HTML',
  css: 'CSS',
  sql: 'SQL',
  ui: 'UI',
  ux: 'UX',
  id: 'ID',
};

function humanize(key: string): string {
  // Pre-protect known acronyms by replacing them with placeholders that
  // survive the camel-case splitter, then restore them at the end.
  const protectedSlots: string[] = [];
  let working = key;
  // Match acronyms that appear at word boundaries (case-insensitive),
  // so `diskUUID` -> `disk{0}` and the placeholder survives splitting.
  const acronymRegex = new RegExp(`\\b(${Object.keys(ACRONYMS).join('|')})\\b`, 'gi');
  working = working.replace(acronymRegex, (match) => {
    const idx = protectedSlots.length;
    protectedSlots.push(ACRONYMS[match.toLowerCase()] ?? match);
    return `__ACR${idx}__`;
  });
  return working
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/__ACR(\d+)__/g, (_, idx: string) => protectedSlots[Number(idx)] ?? '');
}

function defaultLabel(pointer: JsonPointer): string {
  if (pointer === '') return 'Root';
  const last = pointer.slice(pointer.lastIndexOf('/') + 1);
  return humanize(last);
}

/**
 * Try to derive a meaningful label from the data at this pointer.
 * Useful for array items where the pointer segment is just the index.
 * Returns undefined if no confident label can be extracted.
 */
function labelFromValue(
  pointer: JsonPointer,
  value: JsonValue | undefined,
  schema: JsonSchema,
): string | undefined {
  // Only apply when the last pointer segment is a numeric index — otherwise
  // the segment itself is already a decent label.
  const lastSlash = pointer.lastIndexOf('/');
  if (lastSlash < 0) return undefined;
  const lastSeg = pointer.slice(lastSlash + 1);
  if (!/^\d+$/.test(lastSeg)) return undefined;

  const summary = makeSummary(value, schema);
  // If makeSummary returned empty or fell back to "Item N", there's no
  // real label content to use.
  if (!summary || summary.startsWith('Item ')) return undefined;
  return summary;
}

function makePreview(value: JsonValue | undefined, schema: JsonSchema): string {
  if (value === undefined || value === null) return '';
  // If the schema is a oneOf/anyOf without its own type (the meta-schema's
  // `Schema` def is the canonical case), the resolved schema for a
  // child field IS the union — it has no `properties`, no `type`. Infer
  // the appropriate preview path from the value's shape so we don't
  // fall through to a useless JSON.stringify dump.
  let effectiveSchema = schema;
  if (schema.type === undefined && (schema.oneOf || schema.anyOf) && !schema.properties) {
    if (Array.isArray(value)) effectiveSchema = { ...schema, type: 'array' };
    else if (isPlainObject(value)) effectiveSchema = { ...schema, type: 'object' };
  }
  const type = normalizeType(effectiveSchema);
  if (type === 'object') {
    // Record-style object (additionalProperties is a schema, no fixed
    // properties): preview by listing keys or count, not the
    // generic "Untitled" makeSummary fallback.
    const isRecord =
      typeof effectiveSchema.additionalProperties === 'object' &&
      effectiveSchema.additionalProperties !== null &&
      Object.keys(effectiveSchema.properties ?? {}).length === 0;
    if (isRecord) {
      if (!isPlainObject(value)) return '';
      const keys = Object.keys(value);
      if (keys.length === 0) return 'No entries yet';
      if (keys.length <= 4) return keys.join(', ');
      return `${keys.slice(0, 3).join(', ')} (+${keys.length - 3} more)`;
    }
    // Schema-defined object: prefer a name from the value itself
    // (title/name/label) via makeSummary, falling back to a digest of
    // populated keys. Without this fallback, an object-typed property
    // with nested data shows "Empty" until the user adds a title-like
    // field — which is a lie about its state.
    const summary = makeSummary(value, effectiveSchema);
    if (summary) return summary;
    if (isPlainObject(value)) {
      const populated = Object.keys(value).filter((k) => {
        const v = value[k];
        if (v === undefined || v === null) return false;
        if (typeof v === 'string' && v === '') return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return !(isPlainObject(v) && Object.keys(v).length === 0);
      });
      if (populated.length === 0) return '';
      if (populated.length <= 3) return populated.join(', ');
      return `${populated.slice(0, 3).join(', ')} (+${populated.length - 3} more)`;
    }
    return '';
  }
  if (type === 'array') {
    const len = Array.isArray(value) ? value.length : 0;
    return `${len} item${len === 1 ? '' : 's'}`;
  }
  if (typeof value === 'string') {
    return value.length > 60 ? value.slice(0, 60) + '…' : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function makeSummary(
  value: JsonValue | undefined,
  schema: JsonSchema,
  fallbackIndex?: number,
): string {
  const hint = schema['x-bliksys']?.summaryField;
  if (hint && isPlainObject(value) && typeof value[hint] === 'string') {
    return value[hint];
  }
  if (isPlainObject(value)) {
    // Heuristic: prefer title/name/label fields if present.
    for (const key of ['title', 'name', 'label', 'heading']) {
      const v = value[key];
      if (typeof v === 'string' && v.length > 0) return v;
    }
    // Discriminator-based summary for tagged blocks: "columns", "heading".
    const disc = readDiscriminator(value, schema);
    if (disc !== undefined) return disc;
  }
  if (typeof value === 'string' && value.length > 0) {
    return value.length > 60 ? value.slice(0, 60) + '…' : value;
  }
  // Last resort: nothing meaningful to show. Return empty so callers
  // that overlay this as a "preview" don't render visible "Untitled"
  // rows. The "Item N" fallback for arrays still applies — there at
  // least the index is informative.
  return fallbackIndex !== undefined ? `Item ${fallbackIndex + 1}` : '';
}

function readDiscriminator(value: JsonValue | undefined, schema: JsonSchema): string | undefined {
  const prop = schema.discriminator?.propertyName ?? 'type';
  if (!isPlainObject(value)) return undefined;
  const v = value[prop];
  return typeof v === 'string' ? v : undefined;
}

/**
 * Is this schema pinned to a single value? Covers `const` and single-
 * element `enum` (including non-string values). Used to filter out
 * fields that can't be meaningfully edited — typically discriminator
 * tags in a tagged-union member.
 */
function isPinnedToSingleValue(schema: JsonSchema): boolean {
  if (schema.const !== undefined) return true;
  return schema.enum?.length === 1;
}

/**
 * Derive a label from a $ref pointer. "#/$defs/diskDevice" -> "Disk device".
 * Returns undefined if the input isn't a usable ref.
 */
function labelFromRef(schema: JsonSchema): string | undefined {
  if (typeof schema.$ref !== 'string') return undefined;
  const lastSegment = schema.$ref.split('/').pop();
  if (!lastSegment) return undefined;
  return humanize(lastSegment);
}

/**
 * True for schemas that resolve to leaf types (no recursion into
 * properties or items). Handles `oneOf`/`anyOf` by recursing — every
 * variant must itself be scalar. Used to decide whether array items
 * render inline (chip editor) or as zoom-rows.
 *
 * Resolves `$ref` so that a oneOf-of-refs (like the demo's `Block`
 * union, where each variant is `{ $ref: '#/$defs/SomeBlock' }`) gets
 * its true shape inspected — without this, every $ref looks like an
 * empty schema, which `normalizeType` reports as "no type", which the
 * leaf-check then treats as scalar. That's how the bug "blocks array
 * shows as scalar chip editor" creeps in.
 */
/**
 * Reorder a list of [key, schema] entries to match `x-bliksys.propOrder`.
 * Keys listed in propOrder come first, in the listed sequence. Keys
 * not listed retain their original relative order and append after.
 *
 * Stable: two unlisted keys keep their input order; two listed keys
 * keep their listed order.
 */
function sortByPropOrder<T>(
  entries: readonly [string, T][],
  propOrder: readonly string[],
): [string, T][] {
  const orderIndex = new Map<string, number>();
  propOrder.forEach((key, idx) => {
    if (!orderIndex.has(key)) orderIndex.set(key, idx);
  });
  // Two passes preserves original order for unlisted keys without
  // relying on Array.sort stability semantics.
  const listed: [string, T][] = [];
  const unlisted: [string, T][] = [];
  for (const entry of entries) {
    if (orderIndex.has(entry[0])) listed.push(entry);
    else unlisted.push(entry);
  }
  listed.sort((a, b) => orderIndex.get(a[0])! - orderIndex.get(b[0])!);
  return [...listed, ...unlisted];
}

function isScalarSchema(schema: JsonSchema, rootSchema: JsonSchema): boolean {
  const resolved = resolveSchema(schema, rootSchema);
  const variants = resolved.oneOf ?? resolved.anyOf;
  if (variants && variants.length > 0) {
    return variants.every((v) => isScalarSchema(v, rootSchema));
  }
  const type = normalizeType(resolved);
  return !(type === 'object' || type === 'array');
}

/**
 * Last-resort label for primitive variants. Catches the common
 * "constraint-free oneOf-of-types" pattern used in the meta-schema for
 * `default` and `const` ({ oneOf: [{type:'string'}, {type:'number'},
 * {type:'boolean'}, {type:'null'}] }) — without this, those variants
 * surface as "Option 1", "Option 2", etc., which is meaningless.
 */
function labelFromPrimitiveType(schema: JsonSchema): string | undefined {
  if (typeof schema.type !== 'string') return undefined;
  switch (schema.type) {
    case 'string':
      return 'Text';
    case 'number':
      return 'Number';
    case 'integer':
      return 'Integer';
    case 'boolean':
      return 'True/false';
    case 'null':
      return 'None';
    case 'array':
      return 'List';
    case 'object':
      return 'Object';
    default:
      return undefined;
  }
}

function pickVariant(
  variants: readonly OneOfVariant[],
  value: JsonValue | undefined,
  discriminator: string | undefined,
): number | null {
  // String-discriminator tagged-union path. Variants pin a string
  // property like { type: { const: 'page' } }. When a discriminator
  // is declared and the value carries a recognized tag, that path is
  // authoritative.
  if (discriminator) {
    if (!isPlainObject(value)) {
      // Non-object values can't carry a tag. Fall through to shape
      // matching — handles unions that mix object variants with
      // primitive variants (rare but valid).
      if (value === undefined) return null;
      const idx = variants.findIndex((v) => valueMatchesShape(v.schema, value));
      return idx === -1 ? null : idx;
    }
    const discValue = value[discriminator];
    if (typeof discValue === 'string') {
      const idx = variants.findIndex((v) => v.discriminatorValue === discValue);
      if (idx !== -1) return idx;
      // Recognized property but unknown value: don't silently land on
      // an arbitrary variant. Returning null leaves the user with the
      // "pick a type" empty state.
      return null;
    }
    // No discriminator key on the value at all — meaning this value
    // wasn't tagged. Fall through to shape matching: variants that
    // identify themselves by other unique fields (e.g. the meta-schema's
    // Reference variant uses `$ref`, Union uses `oneOf`) will be picked
    // by the structure of the value.
    const idx = variants.findIndex((v) => valueMatchesShape(v.schema, value));
    return idx === -1 ? null : idx;
  }
  // No discriminator: shape-matching is the only path.
  if (value === undefined) return null;
  const matchIdx = variants.findIndex((v) => valueMatchesShape(v.schema, value));
  return matchIdx === -1 ? null : matchIdx;
}

/**
 * Cheap structural check: does `value` plausibly satisfy the variant's
 * declared shape? Inspects `type`, `enum`, and `const`. This is not full
 * validation (we don't recurse into properties or check format) — just
 * enough to disambiguate variant pickers.
 */
function valueMatchesShape(schema: JsonSchema, value: JsonValue): boolean {
  // `enum` and `const` are the strongest signals.
  if (schema.const !== undefined) return deepStrictEquals(schema.const, value);
  if (schema.enum !== undefined) return schema.enum.some((e) => deepStrictEquals(e, value));
  // Fall back to declared type.
  const type = normalizeType(schema);
  if (type !== undefined) {
    const types = Array.isArray(type) ? type : [type];
    let typeMatched = false;
    for (const t of types) {
      if (t === 'string' && typeof value === 'string') typeMatched = true;
      else if (t === 'number' && typeof value === 'number') typeMatched = true;
      else if (t === 'integer' && Number.isInteger(value)) typeMatched = true;
      else if (t === 'boolean' && typeof value === 'boolean') typeMatched = true;
      else if (t === 'null' && value === null) typeMatched = true;
      else if (t === 'array' && Array.isArray(value)) typeMatched = true;
      else if (t === 'object' && isPlainObject(value)) typeMatched = true;
    }
    if (!typeMatched) return false;
  }
  // For object values: required keys must be present, and any property
  // pinned to a `const` value must actually equal that constant. Without
  // this, the meta-schema's Reference (required: ['$ref']) and Union
  // (required: ['oneOf']) variants would both match any plain object,
  // making them indistinguishable in the picker.
  if (isPlainObject(value)) {
    if (Array.isArray(schema.required)) {
      for (const key of schema.required) {
        if (!(key in value)) return false;
      }
    }
    if (schema.properties) {
      for (const [key, propSchema] of Object.entries(schema.properties)) {
        const constraint = propSchema.const;
        if (constraint !== undefined && key in value) {
          if (!deepStrictEquals(constraint, value[key])) return false;
        }
      }
    }
  }
  return true;
}

function deepStrictEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepStrictEquals(v, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    return ak.every((k) =>
      deepStrictEquals((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
    );
  }
  return false;
}

function resolveActiveVariant(
  schema: JsonSchema,
  value: JsonValue | undefined,
  ctx: BuildContext,
): JsonSchema | null {
  const variants = schema.oneOf ?? schema.anyOf;
  if (!variants || !schema.discriminator) return null;
  const disc = schema.discriminator.propertyName;
  if (!isPlainObject(value)) return null;
  const discValue = value[disc];
  if (typeof discValue !== 'string') return null;
  for (const v of variants) {
    const resolved = resolveSchema(v, ctx.rootSchema, undefined, ctx.refRegistry);
    if (readConstProperty(resolved, disc) === discValue) return resolved;
  }
  return null;
}

function looksLikeImage(s: string | null): boolean {
  if (!s) return false;
  return /\.(png|jpe?g|gif|webp|svg|avif)(\?|$)/i.test(s);
}
